/**
 * ChatService
 * Service for sharing content via chat messages
 *
 * Architecture:
 * UseCase → ChatService → FirestoreService
 */

import type { Unsubscribe } from 'firebase/firestore';
import { auth } from '@/data/services/firebase';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import type { JsonObject } from '@/data/types/json';
import type { ChatMessageDto } from '@/data/dtos/chat/chatMessageDto';
import type { ChatMessageChangeDto } from '@/data/dtos/chat/chatMessageChangeDto';
import type { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { ChatMessageRemapper } from '@/domain/remappers/chat/chatMessageRemapper';
import { ChatMessageNodeRemapper } from '@/domain/remappers/chat/chatMessageNodeRemapper';
import { buildUploadFileInfo } from '@/domain/utils/uploadFileInfoBuilder';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MediaType } from '@/domain/enums/mediaType';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { initializeTextEncryptionUseCase } from '@/domain/usecases/textEncryption/initializeTextEncryptionUseCase';
import { prepareChatImageUploadUseCase } from '@/domain/usecases/chatMedia/prepareChatImageUploadUseCase';
import { prepareChatVideoThumbnailUseCase } from '@/domain/usecases/chatMedia/prepareChatVideoThumbnailUseCase';

/** Opaque ciphertext content type for the encrypted image / thumbnail blobs. */
const CIPHERTEXT_CONTENT_TYPE = 'application/octet-stream';

export class ChatService {
  private static instance: ChatService;

  private constructor() {}

  static getInstance(): ChatService {
    if (!this.instance) {
      this.instance = new ChatService();
    }
    return this.instance;
  }

  /**
   * Save a batch of chat messages to Firestore in a single batched write.
   *
   * Pure proxy — encryption of `messageData.content` / `metadataData.last_content`
   * is the caller's responsibility (parity with the single-send path: the
   * `SendTextChatMessageUseCase` -> `ChatService.saveChatMessage` flow encrypts
   * in the use case before handing ciphertext to the service). For batch share,
   * `ShareWithFriendsUseCase` initializes `TextEncryptionService` per peer and
   * substitutes the ciphertext into both fields before invoking this method.
   *
   * @param messages - Array of { groupChatId, messageId, messageData, metadataData }
   *   where `messageData.content` and `metadataData.last_content` already hold
   *   the ciphertext (or the empty/plaintext value when the message has no body).
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  async saveBatchChatMessages(
    messages: Array<{
      groupChatId: string;
      messageId: string;
      messageData: JsonObject;
      metadataData: JsonObject;
    }>
  ): Promise<boolean> {
    return firestoreService.saveBatchChatMessages(messages);
  }

  /**
   * Save a single chat message to Firestore, transactionally with its
   * chat-node metadata. This is the text-send hop used by
   * `SendTextChatMessageUseCase`.
   *
   * Matches Flutter: lib/data/data_sources/chat_data_source.dart:7-49
   * (`ChatDataSource.saveChatMessage`). The metadata payload assembled below
   * is the exact field set from `chat_data_source.dart:24-42`. The message
   * payload is built via `ChatMessageRemapper.toJson` with `content`
   * overridden by the (already-encrypted) ciphertext supplied by the caller
   * — mirroring `chat_repository.dart:14-25` where the repository encrypts
   * `content` before delegating to the data source.
   *
   * The remapper is invoked from the domain service (not the data layer) to
   * keep the data-layer surface JSON-only (data-layer.md surface rule).
   * Entity-to-DTO conversion also happens here so callers stay on the
   * domain-entity surface — consistent with `chatUsersInfo` being an entity
   * (rather than half-DTO / half-entity inputs).
   *
   * @param chatMessage - The message entity to write. The remapper converts
   *   it to its DTO form, then `content` is replaced with `encryptedContent`
   *   before serialization.
   * @param chatUsersInfo - The chat-users context used to assemble the
   *   chat-node metadata document (users, names, timestamps).
   * @param encryptedContent - The value to land in the doc. Three cases,
   *   mirroring Flutter `chat_repository.dart:20-25` + `chat_data_source.dart:35`:
   *     - a non-empty ciphertext string -> written verbatim to both
   *       `last_content` (metadata doc) and `content` (message doc);
   *     - the EMPTY STRING `''` (the video-call / empty-body message: Flutter's
   *       `else` branch keeps `content = ''` and the data source writes
   *       `'last_content': ''`) -> written as `last_content: ''` and
   *       `content: ''`. This MUST stay `''`, not `null`/dropped: a `videoCall`
   *       message is the latest message when it is written, so it sets the
   *       shared chat node's `last_content`, and Flutter's
   *       `ChatMessageNode.lastContent` is a NON-NULLABLE `String` parsed with
   *       no fallback (`chat_message_node.dart:42`) — a `null` there throws
   *       `type 'Null' is not a subtype of type 'String'` and crashes the
   *       Flutter messages-list parse;
   *     - `null` (no textual content at all) -> `last_content: null` and the
   *       `content` key dropped from the message doc.
   * @returns `true` on success, `false` on failure (parity with Flutter's
   *   `bool result` pattern at `chat_data_source.dart:9, 45`).
   */
  async saveChatMessage(
    chatMessage: ChatMessageEntity,
    chatUsersInfo: ChatUsersInfoEntity,
    encryptedContent: string | null,
  ): Promise<boolean> {
    // Bridge entity -> DTO at the service boundary so the caller can stay on
    // the entity surface (consistent with `chatUsersInfo: ChatUsersInfoEntity`).
    const chatMessageDto: ChatMessageDto = ChatMessageRemapper.toDto(chatMessage);

    // The doc-ref groupChatId is sourced from `chatUsersInfo.groupChatId`
    // (mirroring Flutter `chat_data_source.dart:13`), falling back to
    // `chatMessageDto.chatId` — the two are semantically identical at the
    // call site (see `shareWithFriendsUseCase.ts:54` where `chatId: groupChatId`).
    // The fallback exists purely to satisfy the optional nullability on
    // `ChatUsersInfoEntity.groupChatId`; the data-layer parameter is `string`.
    const groupChatId = chatUsersInfo.groupChatId ?? chatMessageDto.chatId;

    // Build the chat-node metadata payload exactly per
    // chat_data_source.dart:24-42. Field order, naming, and the
    // `created_timestamp` fallback rule mirror Flutter precisely.
    const metadataData: JsonObject = {
      users: [chatUsersInfo.currentFirebaseId, chatUsersInfo.peerId],
      chatId: groupChatId,
      idFrom: chatUsersInfo.currentFirebaseId,
      idTo: chatUsersInfo.peerId,
      timestamp: chatMessageDto.timestamp,
      created_timestamp:
        chatUsersInfo.createdTimestamp !== 0
          ? chatUsersInfo.createdTimestamp
          : chatMessageDto.timestamp,
      // `??` coalesces ONLY `null`/`undefined`: an empty-string `''` (video-call
      // / empty-body message) is preserved as `last_content: ''`, matching
      // Flutter `chat_data_source.dart:35` (`'last_content': chatMessage.content`
      // where `content == ''`). Only a `null` `encryptedContent` lands `null`.
      // Keeping `''` here is parity-critical — Flutter's non-nullable
      // `ChatMessageNode.lastContent` parse (chat_message_node.dart:42) crashes
      // on `null`.
      last_content: encryptedContent ?? null,
      type: chatMessageDto.type,
      nameUser1: chatUsersInfo.currentName,
      nameUser2: chatUsersInfo.peerName,
      seenByPeer: false,
      blocked: false,
    };

    // Build the message payload with the encrypted ciphertext as `content`.
    // `??` coalesces ONLY `null`/`undefined`: an empty-string `''` (video-call
    // / empty-body message) is preserved as `content: ''`, matching Flutter
    // `chat_message.dart` (`includeIfNull: false` + `content == ''`, which is
    // NOT null, so the key IS written). `encryptedContent === null` -> drop the
    // `content` key entirely (DTO optional-field convention; the remapper omits
    // `undefined` keys).
    const messageData = ChatMessageRemapper.toJson({
      ...chatMessageDto,
      content: encryptedContent ?? undefined,
    });

    return firestoreService.saveChatMessage(
      groupChatId,
      chatMessageDto.documentId,
      messageData,
      metadataData,
    );
  }

  /**
   * Upload a chat-media file to Firebase Storage as CIPHERTEXT, then fire the
   * transcode Cloud Function (fire-and-forget). This is the chat branch of
   * Flutter's `AppStorageService.uploadChatMedia`
   * (lib/services/app_storage_service.dart:164-214) — but on web, chat media is
   * client-side E2EE so the BYTES that land in Storage differ from Flutter:
   *
   *   - IMAGE branch: the raw file is NEVER uploaded. Instead
   *     `PrepareChatImageUploadUseCase` resizes/compresses both renditions and
   *     AES-GCM-encrypts them, and BOTH ciphertexts are uploaded to the
   *     deterministic `{fileDir}image/{fileName}` / `{fileDir}thumbnail/{fileName}`
   *     paths the reworked `transcodeChatImage` CF reads (path contract:
   *     `MediaUtils.getChatImageCiphertextUploadPaths` ⇔ `transcode_chat_image.ts`).
   *   - VIDEO branch: the raw video IS still uploaded (the server Transcoder
   *     encrypts the segments). Additionally `PrepareChatVideoThumbnailUseCase`
   *     captures + encrypts the thumbnail frame; the encrypted thumbnail is
   *     uploaded to `{fileDir}thumbnail/{fileName}` when present. On the codec
   *     fallback (`encryptedThumbnail: null`) the thumbnail upload is SKIPPED
   *     (the CF then writes no `thumbnail` and the bubble shows the placeholder
   *     + play icon — consideration D); a missing thumbnail must NOT abort.
   *
   * Ordering (image branch):
   *   1. Build `uploadInfo` and the base storage path.
   *   2. Defensively (re-)derive the chat E2EE key for the peer
   *      (`initializeTextEncryptionUseCase`) so `EncryptBinaryUseCase` is NOT in
   *      pass-through mode — without a derived key it would upload PLAINTEXT.
   *      The init has a same-peer fingerprint short-circuit so this is ~free when
   *      the chat-load init (`useChat.ts:756`) already derived the right key.
   *   3. `await prepareChatImageUploadUseCase.execute` → two ciphertexts + paths.
   *   4. `await` both ciphertext uploads. On a thrown upload/prepare error,
   *      return `false` (parity with Flutter's try/catch → `false`,
   *      app_storage_service.dart:210-213).
   *   5. Fire `transcodeChatContent` WITHOUT awaiting (app_storage_service.dart:198-208).
   *
   * Ordering (video branch): same defensive init, then the raw video upload
   * (resolving the `dynamicUrl` download URL the CF ffprobes), then the optional
   * encrypted-thumbnail upload, then the un-awaited transcode fire.
   *
   * The transcode payload keys are byte-identical to
   * `cloud_functions_service.dart:176-186`: `filePath`, `groupChatId`,
   * `chatDocumentId`, `fileExtension`, `aspectRatio`, `timeStartExpression`,
   * `timeDurationExpression`, `resizeWidth`, `resizeHeight`. Web has no
   * trim/crop, so the trim/crop keys are passed `null` (same as user content).
   * Only the uploaded BYTES change (plaintext → ciphertext); the callable fire,
   * its payload, and the post-upload Firestore field-write are unchanged.
   *
   * Orchestration lives in the domain service (not the data layer) so the data
   * layer stays a thin proxy (data-layer.md surface rule).
   *
   * @param file - The media file to upload.
   * @param mediaType - `MediaType.IMAGE` (0) = image, otherwise video (drives
   *   the encrypt branch here and the transcode function name in the data proxy).
   * @param aspectRatio - Media aspect ratio, forwarded to the transcode payload.
   * @param durationSeconds - Media duration in seconds, used to build `uploadInfo`.
   * @param chatUsersInfo - Chat-users context; `currentFirebaseId`/`peerId`
   *   build the storage group-chat id, `groupChatId` is the transcode target.
   * @param chatDocumentId - The pre-generated message document id, forwarded to
   *   the transcode payload so the Cloud Function can update the right doc.
   * @param peerPublicJwk - The conversation peer's public JWK, threaded down from
   *   the send flow so the encrypt step derives the RIGHT peer key before
   *   `EncryptBinaryUseCase` runs (the singleton key slot is shared with the text
   *   + read paths and could be clobbered). When `undefined` (the not-yet-updated
   *   presentation caller), the defensive re-init is skipped and the flow relies
   *   on the chat-load-derived singleton key (`useChat.ts:756`); when explicitly
   *   `null`, the init is invoked with `null` (pass-through — only the degenerate
   *   no-key case). Optional so tsc stays green for the existing caller.
   * @returns `true` when the storage upload(s) succeed, `false` when they throw.
   */
  async uploadChatMedia(args: {
    file: File;
    mediaType: number;
    aspectRatio?: number;
    durationSeconds: number;
    chatUsersInfo: ChatUsersInfoEntity;
    chatDocumentId: string;
    peerPublicJwk?: JsonWebKey | null;
  }): Promise<boolean> {
    const {
      file,
      mediaType,
      aspectRatio,
      durationSeconds,
      chatUsersInfo,
      chatDocumentId,
      peerPublicJwk,
    } = args;

    // (1) Build uploadInfo (image-aware after Task 2) and the base storage path.
    const uploadInfo = buildUploadFileInfo({
      file,
      firebaseUserId: chatUsersInfo.currentFirebaseId,
      durationSeconds,
    });
    const storageGroupChatId = ChatUtils.getStorageGroupChatId(
      chatUsersInfo.currentFirebaseId,
      chatUsersInfo.peerId,
    );
    const uploadPath = MediaUtils.getChatContentUploadFilePath(uploadInfo, storageGroupChatId);

    // (2) Defensive E2EE key (re-)derivation for the peer. The chat-load init
    // (`useChat.ts:756`) already derived this key, but the singleton's single
    // key slot is shared with the text-send + `EncryptedCachedImage` read paths
    // and could have been clobbered for another peer before this send runs. The
    // init's same-peer fingerprint short-circuit makes a correct-key re-init
    // ~free. Skip when the caller did not thread a JWK (existing caller relies
    // on the chat-load init). `EncryptBinaryUseCase` is a pass-through when no
    // key is derived, so this guard is what keeps the upload ciphertext (not
    // plaintext).
    if (peerPublicJwk !== undefined) {
      await initializeTextEncryptionUseCase.execute(peerPublicJwk);
    }

    let downloadUrl: string | null = null;
    try {
      if (mediaType === MediaType.IMAGE) {
        // (3a) IMAGE: produce + upload BOTH ciphertexts to the deterministic
        // paths the reworked `transcodeChatImage` CF reads. The raw file is
        // NEVER uploaded for images.
        const prepared = await prepareChatImageUploadUseCase.execute(file, uploadPath);
        await Promise.all([
          cloudStorageService.uploadChatMediaContent(
            new Blob([prepared.encryptedImage], { type: CIPHERTEXT_CONTENT_TYPE }),
            prepared.imageUploadPath,
            CIPHERTEXT_CONTENT_TYPE,
          ),
          cloudStorageService.uploadChatMediaContent(
            new Blob([prepared.encryptedThumbnail], { type: CIPHERTEXT_CONTENT_TYPE }),
            prepared.thumbnailUploadPath,
            CIPHERTEXT_CONTENT_TYPE,
          ),
        ]);
      } else {
        // (3b) VIDEO: upload the RAW video (the Transcoder encrypts segments
        // server-side), resolving the download URL for the CF's ffprobe
        // `dynamicUrl`. Then capture + encrypt the thumbnail and upload it when
        // present (skip on the codec fallback — a missing thumbnail must not
        // abort the send).
        const result = await cloudStorageService.uploadChatMediaContent(
          file,
          uploadPath,
          uploadInfo.contentType,
        );
        downloadUrl = result.downloadUrl;

        const preparedThumb = await prepareChatVideoThumbnailUseCase.execute(file, uploadPath);
        if (
          preparedThumb.encryptedThumbnail !== null &&
          preparedThumb.thumbnailUploadPath !== null
        ) {
          await cloudStorageService.uploadChatMediaContent(
            new Blob([preparedThumb.encryptedThumbnail], { type: CIPHERTEXT_CONTENT_TYPE }),
            preparedThumb.thumbnailUploadPath,
            CIPHERTEXT_CONTENT_TYPE,
          );
        }
      }
    } catch (error) {
      // Parity with app_storage_service.dart:210-213 — return `false` on any
      // thrown prepare/encrypt/upload error.
      appLogger.error(
        'Error uploading chat media',
        'ChatService.uploadChatMedia',
        { error: error as Error },
      );
      return false;
    }

    // (4) Fire the transcode CF WITHOUT awaiting (app_storage_service.dart:198-208).
    // The data proxy swallows + logs its own errors so this cannot reject.
    //
    // `dynamicUrl` mirrors the user-gen flow (userMediaService.publishContent →
    // transcodeUserContent): the video CF ffprobes this REMOTE URL for metadata
    // instead of downloading the whole file (avoids OOM/timeout on long videos).
    // Images have no raw upload (so no download URL) and the image CF ignores
    // `dynamicUrl`, so `null` is sent for images.
    const transcodePayload: JsonObject = {
      filePath: uploadPath,
      dynamicUrl: downloadUrl,
      groupChatId: chatUsersInfo.groupChatId ?? null,
      chatDocumentId,
      fileExtension: uploadInfo.fileExtension,
      aspectRatio: aspectRatio ?? null,
      timeStartExpression: null,
      timeDurationExpression: null,
      resizeWidth: null,
      resizeHeight: null,
    };
    void cloudFunctionsService.transcodeChatContent(transcodePayload, mediaType);

    // (5) Storage upload(s) succeeded.
    return true;
  }

  /**
   * Fetch one page of chat messages, newest-first, and remap raw Firestore JSON
   * into `ChatMessageDto`s (data-layer surface rule: the data layer returns
   * `JsonObject[]`; the domain service is where DTO mapping happens).
   *
   * Matches Flutter: lib/presentation/screens/chat/chat_screen.dart:398-479 —
   * `FirebaseFirestore.instance.collection('message_nodes').doc(groupChatId)
   *   .collection('messages').orderBy('timestamp', descending: true)
   *   .startAfterDocument(_lastFetchedDocument).limit(_documentLimit).get()`.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param lastTimestamp - The timestamp of the last document from the previous
   *   page, or `null` for the first page.
   * @param limit - Page size (typically `SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   * @returns `messages` (mapped DTOs, newest-first) and `lastTimestamp` (the
   *   timestamp of the last doc in this page, or `null` if the page is empty).
   */
  async fetchMessagesPage(
    groupChatId: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<{ messages: ChatMessageDto[]; lastTimestamp: number | null }> {
    const { messages: rawMessages, lastTimestamp: nextLastTimestamp } =
      await firestoreService.fetchChatMessagesPage(groupChatId, lastTimestamp, limit);

    const messages = rawMessages.map((raw) => ChatMessageRemapper.fromJson(raw));

    return { messages, lastTimestamp: nextLastTimestamp };
  }

  /**
   * Fetch one page of chat NODES (the inbox direct-messages list), newest-first,
   * and remap each raw Firestore `/message_nodes/{id}` doc into a
   * `ChatMessageNodeEntity` (data-layer surface rule: the data layer returns
   * `JsonObject[]`; the domain service is where the remap happens).
   *
   * Thin fetch+remap — peer decryption of `text`-type rows' `lastContent` is
   * the use case's job (`FetchChatNodesUseCase`), consistent with
   * `fetchMessagesPage`. Note this path surfaces ENTITIES (camelCase
   * `lastContent`), not DTOs, so the use case decrypts into `lastContent`, not
   * the DTO's `last_content`.
   *
   * Matches Flutter: lib/presentation/screens/messages/messages_screen.dart:251-265 —
   * `collection('message_nodes').where('users', arrayContains: _myUser.firebaseId)
   *   .where('blocked', isEqualTo: false).orderBy('timestamp', descending: true)
   *   .startAfterDocument(_lastFetchedChatDocument).limit(_chatDocumentLimit).get()`.
   *
   * @param userId - The current user's Firebase id (the `array-contains` member).
   * @param lastTimestamp - The timestamp of the last document from the previous
   *   page, or `null` for the first page.
   * @param limit - Page size (`SERVER_FETCH_CONSTANTS.MESSAGES_LIST_PAGINATION = 20`).
   * @returns `nodes` (mapped entities, newest-first) and `lastTimestamp` (the
   *   timestamp of the last doc in this page, or `null` if the page is empty).
   */
  async fetchChatNodesPage(
    userId: string,
    lastTimestamp: number | null,
    limit: number,
  ): Promise<{ nodes: ChatMessageNodeEntity[]; lastTimestamp: number | null }> {
    const { nodes: rawNodes, lastTimestamp: nextLastTimestamp } =
      await firestoreService.getChatNodesPage(userId, lastTimestamp, limit);

    const nodes = rawNodes.map((raw) =>
      ChatMessageNodeRemapper.fromDto(ChatMessageNodeRemapper.fromJson(raw)),
    );

    return { nodes, lastTimestamp: nextLastTimestamp };
  }

  /**
   * Subscribe to live chat-NODE changes for `userId`'s direct-message list
   * (the inbox), remapping each change's raw Firestore doc into a
   * `ChatMessageNodeEntity` via `ChatMessageNodeRemapper.fromDto(fromJson(...))`
   * before delivery so the consumer never sees raw JSON (data-layer surface rule).
   *
   * Thin proxy over `firestoreService.subscribeToChatNodes`. Per-change peer
   * decryption of `text`-type rows' `lastContent` is the use case's job
   * (`SubscribeToChatNodesUseCase`), consistent with `fetchChatNodesPage` vs
   * `FetchChatNodesUseCase` — the service stays decrypt-free. This path
   * surfaces ENTITIES (camelCase `lastContent`), so the use case decrypts into
   * `lastContent`, not the DTO's `last_content`.
   *
   * Matches Flutter: lib/presentation/screens/messages/messages_screen.dart:459-476
   * (`_subscribeToChatUpdates`). The `timestamp` filter is conditional and is
   * realized entirely in the Task-2 query: `sinceTimestamp` is forwarded
   * UNCHANGED (including `null`/`undefined`). A numeric value yields the `>=`
   * cursor (non-empty first page → `_mostRecentChatDocument != null`); a
   * `null`/`undefined` value yields the unfiltered subscription (empty first
   * page → `_mostRecentChatDocument == null`, lines 474-475).
   *
   * @param userId - The current user's Firebase id (the `array-contains` member).
   * @param sinceTimestamp - The most-recent chat timestamp from the first page,
   *   or `null`/`undefined` when the first page was empty. Forwarded verbatim to
   *   the data layer, which conditionally applies the `>=` filter.
   * @param onChange - Called once per snapshot batch with the array of changes.
   *   Each change carries a `ChatMessageNodeEntity` produced by
   *   `ChatMessageNodeRemapper.fromDto(fromJson(...))`.
   * @param onError - Optional error sink for the underlying snapshot listener.
   * @returns An `Unsubscribe` handle the caller is responsible for invoking.
   */
  subscribeToChatNodes(
    userId: string,
    sinceTimestamp: number | null | undefined,
    onChange: (
      changes: Array<{ type: RemoteDocumentChangeType; data: ChatMessageNodeEntity }>,
    ) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToChatNodes(
      userId,
      sinceTimestamp,
      (rawChanges) => {
        const changes = rawChanges.map((c) => ({
          type: c.type,
          data: ChatMessageNodeRemapper.fromDto(ChatMessageNodeRemapper.fromJson(c.data)),
        }));
        onChange(changes);
      },
      onError,
    );
  }

  /**
   * Subscribe to live chat-message changes arriving after `sinceTimestamp`,
   * remapping each raw Firestore doc into a `ChatMessageDto` before delivery
   * so the consumer never sees raw JSON (data-layer surface rule).
   *
   * Matches Flutter: lib/presentation/screens/chat/chat_screen.dart:481-623 —
   * `where('timestamp', isGreaterThan: <mostRecentTimestamp>).snapshots()`,
   * with per-change `added` / `modified` / `removed` fan-out at the consumer.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param sinceTimestamp - Lower-exclusive cursor; the snapshot does NOT
   *   re-deliver any doc with `timestamp <= sinceTimestamp` (the initial page
   *   fetched via `fetchMessagesPage` is therefore not echoed back).
   * @param onChange - Called once per snapshot batch with the array of changes.
   *   Each change carries a `ChatMessageDto` produced by
   *   `ChatMessageRemapper.fromJson`.
   * @param onError - Optional error sink for the underlying snapshot listener.
   * @returns An `Unsubscribe` handle the caller is responsible for invoking.
   */
  subscribeToNewMessages(
    groupChatId: string,
    sinceTimestamp: number,
    onChange: (changes: ChatMessageChangeDto[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToChatMessages(
      groupChatId,
      sinceTimestamp,
      (rawChanges) => {
        const changes: ChatMessageChangeDto[] = rawChanges.map((c) => ({
          type: c.type,
          data: ChatMessageRemapper.fromJson(c.data),
        }));
        onChange(changes);
      },
      onError,
    );
  }

  /**
   * Mark the latest peer message as seen by flipping `seenByPeer: true` on
   * both the message doc and the chat-node metadata doc in a single
   * Firestore transaction.
   *
   * Matches Flutter: lib/data/data_sources/chat_data_source.dart:100-126
   * (`ChatDataSource.setPeerMessageSeen`). The Flutter side guards on
   * `chatMessage.seenByPeer == false` at the call site — that no-op guard
   * lives at the use-case layer on web (`MarkPeerMessageSeenUseCase`); this
   * service method is a pure proxy and ALWAYS issues the write.
   *
   * @param groupChatId - The chat's group id (sorted-hashCode join of the two user ids).
   * @param messageId - The message document id (the `documentId` on `ChatMessage`).
   */
  async markPeerMessageSeen(groupChatId: string, messageId: string): Promise<void> {
    await firestoreService.setPeerMessageSeen(groupChatId, messageId);
  }

  /**
   * Toggle blocking a conversation with `peerId` by updating the CURRENT
   * user's own `users/{uid}.blockedConversations` array.
   *
   * Resolves the current user via `auth.currentUser`. If there is no
   * authenticated user, returns `false` without writing — parity with
   * Flutter's `firebaseUser == null` -> `return false` guard.
   *
   * Matches Flutter: lib/data/data_sources/users_data_source.dart:340-362
   * (`UsersDataSource.setBlockConversation`). The optimistic local write +
   * rollback wrapper (Flutter `ChatRepository.setBlockConversation`,
   * chat_repository.dart:27-37) is NOT done here — that is the use case's
   * responsibility (Task 2), mirroring Flutter where the local write lives in
   * the repository, not the data source.
   *
   * @param peerId - The peer's Firebase id to add/remove from the array.
   * @param block - `true` to block, `false` to unblock.
   * @returns `true` on success, `false` if there is no authenticated user or
   *   the write fails.
   */
  async setBlockConversation(peerId: string, block: boolean): Promise<boolean> {
    const currentUser = auth.currentUser;
    if (currentUser == null) {
      appLogger.error(
        'No authenticated user for block conversation',
        'ChatService.setBlockConversation',
      );
      return false;
    }

    return firestoreService.setBlockConversation(currentUser.uid, peerId, block);
  }

  /**
   * Toggle muting `peerId` by updating the CURRENT user's own
   * `users/{uid}.mutedUsers` array.
   *
   * Resolves the current user via `auth.currentUser`. If there is no
   * authenticated user, returns `false` without writing — parity with
   * Flutter's `firebaseUser == null` -> `return false` guard
   * (lib/data/data_sources/users_data_source.dart:367-369).
   *
   * Matches Flutter: lib/data/data_sources/users_data_source.dart:364-386
   * (`UsersDataSource.setMuteUser`). The optimistic local write + rollback
   * wrapper (Flutter `ChatRepository.setMuteUser`,
   * chat_repository.dart:46-56) is NOT done here — that is the use case's
   * responsibility (Task 3), mirroring Flutter where the local write lives in
   * the repository, not the data source.
   *
   * @param peerId - The peer's Firebase id to add/remove from the array.
   * @param mute - `true` to mute, `false` to unmute.
   * @returns `true` on success, `false` if there is no authenticated user or
   *   the write fails.
   */
  async setMuteUser(peerId: string, mute: boolean): Promise<boolean> {
    const currentUser = auth.currentUser;
    if (currentUser == null) {
      appLogger.error(
        'No authenticated user for mute user',
        'ChatService.setMuteUser',
      );
      return false;
    }

    return firestoreService.setMuteUser(currentUser.uid, peerId, mute);
  }
}

// Export singleton instance for convenience
export const chatService = ChatService.getInstance();
